import { useEffect, useState, useMemo } from 'react';
import Cookies from 'js-cookie';
import * as ActionKeys from '../../src/store/ActionKeys';
import { UserContext, IUserContextProps } from '../context/UserContext';
import { useStateValue } from '../hooks/UseStateValue';
import { useConfigContext } from '../context/ConfigContext';
import { useLoggerContext } from '../context/LoggerContext';
import { isValidAge, validateCpr } from '../helpers/cpr-validation';
import { useLoggedInCustomerStatus } from '../hooks/useLoggedInCustomerStatus';
import { Stakeholder, TProvider } from '../interfaces/interfaces';
import { callBBR } from '../helpers/shared';

const UserProvider = ({ children }: TProvider) => {
    const [loggedIn, setLoggedIn] = useState<boolean>();
    const [{ selection, model, shouldCheckBBR }, dispatch] = useStateValue();
    const [stakeholder, setStakeholder] = useState<Stakeholder>({} as Stakeholder);
    const [ageValid, setAgeValid] = useState<boolean>(true);
    const [cprValid, setCprValid] = useState<boolean>(true);
    const [isBornholm, setIsBornholm] = useState<boolean>(false);
    const [canProceed, setCanProceed] = useState<boolean>(true);
    const [age, setAge] = useState<number | undefined>();
    const [addressInBBR, setAddressInBBR] = useState<boolean | undefined>(undefined);
    const [isNeedOtherCoverage, setIsNeedOtherCoverage] = useState<boolean>(false);
    const config = useConfigContext();
    const { logCallBBRError } = useLoggerContext();
    const customerIdVariable = window.topContext.user.getActiveUser().customerIdList;

    let customerId: string;
    if (Array.isArray(customerIdVariable)) {
        customerId = customerIdVariable[0];
    } else {
        customerId = customerIdVariable;
    }
    // custom hook to check for customer marking
    useLoggedInCustomerStatus();

    useEffect(() => {
        const jwtCookie = Cookies.get('jwt');
        setLoggedIn(!!jwtCookie);
    }, []);

    useEffect(() => {
        const sh = model[config.product]?.stakeholder;
        if (sh) {
            setStakeholder(sh);
        }

        const cprNo = sh?.cprNo ?? selection.cprNo ?? '';
        const v = validateCpr(cprNo);
        setAge(v.age);
        setCprValid(v.valid);
        if (v.valid) {
            setAgeValid(!!v.age && isValidAge(v.age));
        } else {
            setAgeValid(true);
        }
        const mcpc = model[config.product].customerMarking;
        if (mcpc) {
            setCanProceed(mcpc.canProceed);
        }
        setIsNeedOtherCoverage(selection.isNeedOtherCoverage ?? false);
    }, [model, selection.cprNo, config.product, selection.isNeedOtherCoverage]);

    // is a zipcode for Bornholm?
    const zipcodeIsBornholm = (zipCode: number | string): boolean => {
        if (typeof zipCode === 'number') {
            return zipCode >= 3700 && zipCode <= 3799;
        } else {
            return /^37\d{2}.*$/.test(zipCode);
        }
    };

    useEffect(() => {
        setIsBornholm(
            zipcodeIsBornholm(
                selection.address?.addressInfo?.zipCode ?? model[config.product].stakeholder?.zipCity ?? ''
            )
        );
        if (shouldCheckBBR && selection.address?.kvhxUrl) {
            dispatch({ type: ActionKeys.SET_SHOULD_CHECK_BBR, payload: false });
            callBBR(selection.address?.kvhxUrl)
                .then((bbr) => {
                    setAddressInBBR(bbr);
                })
                .catch((error: any) => {
                    logCallBBRError(error.message);
                });
        }
    }, [config.product, dispatch, model, selection, shouldCheckBBR, logCallBBRError]);

    const items: IUserContextProps = useMemo(
        () => ({
            loggedIn,
            customerId,
            stakeholder,
            ageValid,
            cprValid,
            age,
            addressInBBR,
            isBornholm,
            canProceed,
            isNeedOtherCoverage
        }),
        [
            loggedIn,
            customerId,
            stakeholder,
            ageValid,
            cprValid,
            age,
            addressInBBR,
            isBornholm,
            canProceed,
            isNeedOtherCoverage
        ]
    );

    return <UserContext.Provider value={items}>{children}</UserContext.Provider>;
};

export default UserProvider;
